import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";

export default function LegalPage() {
  const { lang, t } = useLang();

  const isFr = lang === "fr";

  return (
    <div data-testid="legal-page">
      <Section className="pt-32 md:pt-36">
        <Container>
          <div className="max-w-4xl">
            <Reveal><Eyebrow>{isFr ? "Informations légales" : "Legal information"}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6">
                {isFr ? "Mentions légales." : "Legal notice."}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {isFr
                  ? "Informations relatives à l’éditeur du site, aux données personnelles et à l’utilisation des cookies."
                  : "Information about the publisher of this site, personal data and the use of cookies."}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Container className="pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
          {/* Side nav */}
          <aside className="lg:col-span-3">
            <Reveal>
              <nav className="sticky top-28 space-y-3 text-[12px] tracking-[0.22em] uppercase">
                <a href="#editeur" className="block text-arven-offwhite/85 hover:text-arven-gold transition-colors">
                  {isFr ? "Éditeur" : "Publisher"}
                </a>
                <a href="#hebergement" className="block text-arven-offwhite/85 hover:text-arven-gold transition-colors">
                  {isFr ? "Hébergement" : "Hosting"}
                </a>
                <a href="#propriete" className="block text-arven-offwhite/85 hover:text-arven-gold transition-colors">
                  {isFr ? "Propriété intellectuelle" : "Intellectual property"}
                </a>
                <a href="#confidentialite" className="block text-arven-offwhite/85 hover:text-arven-gold transition-colors">
                  {isFr ? "Confidentialité" : "Privacy"}
                </a>
                <a href="#cookies" className="block text-arven-offwhite/85 hover:text-arven-gold transition-colors">
                  Cookies
                </a>
              </nav>
            </Reveal>
          </aside>

          {/* Content */}
          <div className="lg:col-span-9 space-y-16">
            <Reveal>
              <section id="editeur" className="scroll-mt-32">
                <h2 className="serif text-3xl md:text-4xl mb-6">{isFr ? "Éditeur du site" : "Site publisher"}</h2>
                <div className="space-y-4 text-[15px] md:text-base leading-[1.85] text-arven-offwhite/80">
                  <p>
                    {isFr ? (
                      <>ARVÈN Cuisines est une marque de <strong className="text-arven-offwhite">Homely Solutions Sàrl</strong>.</>
                    ) : (
                      <>ARVÈN Cuisines is a brand of <strong className="text-arven-offwhite">Homely Solutions Sàrl</strong>.</>
                    )}
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 mt-6 border-t border-white/10 pt-6">
                    <dt className="label-eyebrow">{isFr ? "Raison sociale" : "Company name"}</dt>
                    <dd className="sm:col-span-2">Homely Solutions Sàrl</dd>

                    <dt className="label-eyebrow">{isFr ? "Marque" : "Brand"}</dt>
                    <dd className="sm:col-span-2">ARVÈN Cuisines</dd>

                    <dt className="label-eyebrow">{isFr ? "Siège social" : "Registered office"}</dt>
                    <dd className="sm:col-span-2">
                      Rue Jean-André Venel 4<br />
                      1400 Yverdon-les-Bains<br />
                      Suisse
                    </dd>

                    <dt className="label-eyebrow">Email</dt>
                    <dd className="sm:col-span-2">
                      <a href="mailto:contact@arven-cuisines.com" className="hover:text-arven-gold transition-colors">
                        contact@arven-cuisines.com
                      </a>
                    </dd>

                    <dt className="label-eyebrow">{isFr ? "Téléphone" : "Phone"}</dt>
                    <dd className="sm:col-span-2">
                      <a href="tel:+41215394309" className="hover:text-arven-gold transition-colors">
                        +41 21 539 43 09
                      </a>
                    </dd>
                  </dl>
                </div>
              </section>
            </Reveal>

            <Reveal>
              <section id="hebergement" className="scroll-mt-32">
                <h2 className="serif text-3xl md:text-4xl mb-6">{isFr ? "Hébergement" : "Hosting"}</h2>
                <p className="text-[15px] md:text-base leading-[1.85] text-arven-offwhite/80">
                  {isFr
                    ? "Le site arven-cuisines.com est hébergé sur une infrastructure cloud sécurisée. Pour toute demande relative à l’hébergement, merci de nous contacter à contact@arven-cuisines.com."
                    : "The arven-cuisines.com site is hosted on a secure cloud infrastructure. For any hosting-related queries, please contact us at contact@arven-cuisines.com."}
                </p>
              </section>
            </Reveal>

            <Reveal>
              <section id="propriete" className="scroll-mt-32">
                <h2 className="serif text-3xl md:text-4xl mb-6">{isFr ? "Propriété intellectuelle" : "Intellectual property"}</h2>
                <div className="space-y-4 text-[15px] md:text-base leading-[1.85] text-arven-offwhite/80">
                  <p>
                    {isFr
                      ? "L’ensemble des contenus présents sur ce site (textes, photographies, visuels, logos, marques, mise en page) est la propriété exclusive de Homely Solutions Sàrl ou de ses partenaires. Toute reproduction, représentation, modification ou exploitation, partielle ou totale, sans autorisation écrite préalable est interdite."
                      : "All content published on this site (text, photographs, images, logos, trademarks, layout) is the exclusive property of Homely Solutions Sàrl or its partners. Any reproduction, representation, modification or exploitation, in part or in whole, without prior written authorization is prohibited."}
                  </p>
                  <p>
                    {isFr
                      ? "La marque ARVÈN Cuisines et son identité visuelle sont protégées."
                      : "The ARVÈN Cuisines brand and its visual identity are protected."}
                  </p>
                </div>
              </section>
            </Reveal>

            <Reveal>
              <section id="confidentialite" className="scroll-mt-32">
                <h2 className="serif text-3xl md:text-4xl mb-6">{isFr ? "Confidentialité des données" : "Data privacy"}</h2>
                <div className="space-y-4 text-[15px] md:text-base leading-[1.85] text-arven-offwhite/80">
                  <p>
                    {isFr
                      ? "Les informations transmises via nos formulaires (contact, demande de rendez-vous, partenariat) sont collectées par Homely Solutions Sàrl dans le seul but de traiter votre demande et d’assurer le suivi de notre relation commerciale."
                      : "Information submitted through our forms (contact, appointment request, partnership) is collected by Homely Solutions Sàrl for the sole purpose of processing your request and following up on our commercial relationship."}
                  </p>
                  <p>
                    {isFr
                      ? "Conformément à la législation suisse sur la protection des données (LPD) et au RGPD pour les visiteurs européens, vous disposez d’un droit d’accès, de rectification et de suppression de vos données. Pour exercer ces droits, contactez-nous à contact@arven-cuisines.com."
                      : "In accordance with the Swiss Federal Data Protection Act and the GDPR for European visitors, you have the right to access, rectify and delete your data. To exercise these rights, contact us at contact@arven-cuisines.com."}
                  </p>
                  <p>
                    {isFr
                      ? "Vos données ne sont jamais cédées ni revendues à des tiers."
                      : "Your data is never sold or transferred to third parties."}
                  </p>
                </div>
              </section>
            </Reveal>

            <Reveal>
              <section id="cookies" className="scroll-mt-32">
                <h2 className="serif text-3xl md:text-4xl mb-6">Cookies</h2>
                <p className="text-[15px] md:text-base leading-[1.85] text-arven-offwhite/80">
                  {isFr
                    ? "Le site utilise uniquement des cookies techniques nécessaires à son bon fonctionnement (notamment pour mémoriser votre préférence de langue). Aucun cookie publicitaire ou de pistage tiers n’est déposé sans votre consentement explicite."
                    : "This site only uses technical cookies necessary for its proper operation (notably to remember your language preference). No advertising or third-party tracking cookies are placed without your explicit consent."}
                </p>
              </section>
            </Reveal>
          </div>
        </div>
      </Container>

      <Section className="bg-arven-anthracite border-t border-white/5 noise-overlay relative">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight">
                {isFr ? "Une question ? Nous sommes à votre écoute." : "A question? We are here to listen."}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex justify-center gap-4 flex-wrap">
                <Link to="/contact" className="btn-gold">{t.common.contact} <ArrowRight size={14} /></Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
